import isAnyRecorderInTeamOrOpenPosition from "astrid-web/src/features/calendar/helpers/isAnyRecorderInTeamOrOpenPosition";

import { getCollectionData, getDocumentData, runBatch } from "../../helpers";

import createProductionPriceData from "../productions/prices/createProductionPriceData";

import sessionStatuses from "./constants/sessionStatuses";
import createSessionData from "./data/createSessionData";
import createSessionProductionData from "./data/createSessionProductionData";
import { Session } from "./types/Session";

export default async function createOrUpdateSession(
	firebase,
	{
		session,
		price,
		priceId,
		agreement,
		productionId,
		user,
		studio,
		group,
		reader,
		recorder,
		start,
		end,
		polish,
		sessions = [],
	},
) {
	const db = firebase.firestore();

	const production = await getDocumentData(db.collection("productions").doc(productionId));

	if (!production) {
		throw new Error("Production not found");
	}

	const isAnyRecorderOrOpenPosition = isAnyRecorderInTeamOrOpenPosition({ recorder });

	const [producer, readerUsers, recorderUser] = await Promise.all([
		getDocumentData(db.collection("organizations").doc(production.producer)),
		reader.length > 0
			? getCollectionData(db.collection("users").where(firebase.firestore.FieldPath.documentId(), "in", reader))
			: [],
		!isAnyRecorderOrOpenPosition && getDocumentData(db.collection("users").doc(recorder)),
	]);

	if (!producer) {
		throw new Error("Producer not found");
	}

	if (readerUsers.length !== reader.length) {
		throw new Error("Readers not found");
	}

	if (!recorderUser && !isAnyRecorderOrOpenPosition) {
		throw new Error("Recorder not found");
	}

	const dayStart = new Date(start);
	dayStart.setHours(0, 0, 0, 0);

	const sameDaySessions = await getCollectionData(
		db
			.collection("sessions")
			.where("producer", "==", production.producer)
			.where("start", ">=", dayStart)
			.where("start", "<", end),
	);

	const allSessions = [...new Map([...sessions, ...sameDaySessions].map((s) => [s.id, s])).values()];

	const sessionData = Session.parse(
		createSessionData(firebase, {
			session,
			price,
			priceId,
			agreement,
			production,
			user,
			studio,
			group,
			reader,
			readerUsers,
			recorder,
			recorderUser,
			start,
			end,
			polish,
			sessions: allSessions,
		}),
	);

	const productionData = createSessionProductionData(firebase, { production, sessionData });

	const productionSessions = await getCollectionData(
		db.collection("sessions").where("production", "==", productionId),
	);

	const otherProductionSessions = productionSessions.filter(
		({ id, status }) => id !== session?.id && status !== sessionStatuses.CANCELLED,
	);

	const sortedSessions = [...otherProductionSessions, sessionData].sort((a, b) => a.start - b.start);

	await runBatch(db, (batch) => {
		if (session?.productionPriceRef) {
			batch.delete(session.productionPriceRef);
		}

		if (price) {
			const productionPrice = createProductionPriceData(firebase, {
				...price,
				expense: true,
				approved: true,
				producer,
				agreement,
				production,
				referencePrice: price,
				session: sessionData,
			});

			batch.set(productionPrice.ref, productionPrice);

			batch.set(sessionData.ref, {
				...sessionData,
				productionPriceRef: productionPrice.ref,
			});
		} else {
			batch.set(sessionData.ref, {
				...sessionData,
				productionPriceRef: null,
			});
		}

		if (Object.keys(productionData).length) {
			batch.update(production.ref, productionData);
		}

		sortedSessions.forEach((session, index) => {
			batch.set(session.ref, { first: index === 0 }, { merge: true });
		});
	});

	return sessionData;
}
